import React, { useState } from 'react';
import { Button } from '../../ui/Button';

export interface ModerationReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  decision: 'approved' | 'removed';
  isLoading?: boolean;
  reviewTitle?: string;
}

/**
 * Modal for entering a reason when approving or removing a flagged review.
 * Requirements: 9.2, 9.3, 10.2, 10.3
 */
export const ModerationReasonModal = ({
  isOpen,
  onClose,
  onSubmit,
  decision,
  isLoading = false,
  reviewTitle,
}: ModerationReasonModalProps) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError('Please provide a reason for your decision');
      return;
    }
    setError(null);
    onSubmit(reason.trim());
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const isApproval = decision === 'approved';
  const title = isApproval ? 'Approve Review' : 'Remove Review';
  const description = isApproval
    ? 'This review will be restored and visible to the public. The flagger will be notified that the review was deemed appropriate.'
    : 'This review will remain hidden from the public. The review author will be notified of the removal.';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="moderation-modal-title"
    >
      <div className="bg-gray-50 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h3 id="moderation-modal-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h3>
          {reviewTitle && (
            <p className="mt-1 text-sm text-gray-500 truncate">
              Review: &quot;{reviewTitle}&quot;
            </p>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">{description}</p>

          <div>
            <label htmlFor="moderation-reason" className="block text-sm font-medium text-gray-700 mb-2">
              Reason for decision <span className="text-red-500">*</span>
            </label>
            <textarea
              id="moderation-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError(null);
              }}
              placeholder={
                isApproval
                  ? 'Explain why this review is appropriate...'
                  : 'Explain why this review is being removed...'
              }
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-info-500 resize-none ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
              aria-describedby={error ? 'reason-error' : undefined}
            />
            {error && (
              <p id="reason-error" className="mt-1 text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant={isApproval ? 'primary' : 'secondary'}
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading}
            className={!isApproval ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
          >
            {isLoading ? 'Processing...' : isApproval ? 'Approve Review' : 'Remove Review'}
          </Button>
        </div>
      </div>
    </div>
  );
};
